import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { EXTERNAL_ROLES } from "constants/predefined-roles.constants";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";

interface RoleSelectionDialogProps {
  /** Controls visibility. The dialog has no close affordance, so the parent
   *  must flip this back to false (typically by re-checking the cached role
   *  after `onSaved` fires). */
  open: boolean;
  /** The user's previous free-text role (e.g. "Senior Engineer"). When set,
   *  it is shown verbatim in the body so the user understands why they are
   *  being asked again. Omit / empty for users who never had a role. */
  previousRole?: string;
  /** Fired after the new role has been persisted to CDC and patched into the
   *  local `cdcUser` cache. The parent should re-read the cache to dismiss. */
  onSaved: () => void;
}

/**
 * Blocking modal that asks an external user to pick a role from
 * `EXTERNAL_ROLES` when their cached `cov_job_title` is missing or is a
 * legacy free-text value (CX-2385). Non-dismissable: no close icon, no
 * backdrop dismiss, escape disabled. Persists via
 * `window.gigya?.accounts.setAccountInfo` and patches the local cache so
 * the dialog dismisses even if the follow-up `getAccountInfo` refresh
 * fails to return a profile. If the Gigya SDK has not loaded (CDN failure
 * or CSP block) the user sees an error and can retry without the button
 * locking.
 */
export const RoleSelectionDialog: FC<RoleSelectionDialogProps> = ({
  open,
  previousRole,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const hasLegacyRole = !!previousRole && previousRole.length > 0;

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedRole(event.target.value);
  };

  const handleSave = () => {
    if (!selectedRole || submitting) return;
    // The Gigya SDK is loaded asynchronously; if it failed to load (CDN
    // blocked, CSP, etc.) we surface an error rather than locking the
    // button by flipping `submitting` first.
    if (!window.gigya?.accounts) {
      setError(true);
      return;
    }
    setSubmitting(true);
    setError(false);
    window.gigya.accounts.setAccountInfo({
      data: {
        work: {
          cov_job_title: selectedRole,
        },
      } as never,
      callback: (response) => {
        if (response.errorCode !== 0) {
          setSubmitting(false);
          setError(true);
          return;
        }
        // The role is now persisted in CDC. Patch the cached cdcUser so the
        // dialog dismisses on the next render even if the getAccountInfo
        // refresh below returns no profile.
        try {
          const raw = localStorage.getItem("cdcUser");
          if (raw) {
            const cached = JSON.parse(raw);
            cached.extData = cached.extData ?? {};
            cached.extData.work = cached.extData.work ?? {};
            cached.extData.work.cov_job_title = selectedRole;
            localStorage.setItem("cdcUser", JSON.stringify(cached));
          }
        } catch {
          // Ignore: a corrupt cdcUser is replaced by the refresh below or by
          // the next login.
        }
        setSubmitting(false);
        onSaved();
        // Best-effort full refresh so other cached fields stay in sync; the
        // dialog has already dismissed regardless of the outcome.
        window.gigya?.accounts.getAccountInfo({
          extraProfileFields: "work",
          callback: (accountResponse) => {
            if (!accountResponse?.profile) return;
            const cdcUserResponse: Record<string, unknown> = {
              firstName: accountResponse.profile.firstName,
              lastName: accountResponse.profile.lastName,
              email: accountResponse.profile.email,
              country: accountResponse.profile.country,
              locale: accountResponse.profile.locale,
              company: (accountResponse.data?.work as { cov_company?: string })
                ?.cov_company,
              extData: accountResponse.data,
            };
            localStorage.setItem("cdcUser", JSON.stringify(cdcUserResponse));
          },
        });
      },
    });
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="role-selection-dialog-title"
      sx={{ "& .MuiPaper-root": { maxWidth: "480px" } }}
    >
      <DialogTitle id="role-selection-dialog-title">
        {t("rs_title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {hasLegacyRole
            ? t("rs_description_with_legacy", { previousRole })
            : t("rs_description_missing")}
        </DialogContentText>
        <FormControl fullWidth>
          <InputLabel id="role-selection-label" shrink>
            {t("rs_label")}
          </InputLabel>
          <Select
            labelId="role-selection-label"
            id="role-selection-select"
            value={selectedRole}
            label={t("rs_label")}
            onChange={handleChange}
            displayEmpty
            notched
            renderValue={(value) => value || t("rs_placeholder")}
          >
            {EXTERNAL_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("rs_save_failed")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedRole || submitting}
        >
          {t("rs_submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
